import { Skeleton } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { useMemo } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { AssignmentEditRoute } from './AssignmentEditRoute';
import { AssignmentApplyStrategyRoute } from 'borrower/routes/assignment/AssignmentApplyStrategyRoute';
import { AssignmentEditByCSVRoute } from 'borrower/routes/assignment/AssignmentEditByCSVRoute';
import { AssignmentSavedVersionsRoute } from 'borrower/routes/assignment/AssignmentSavedVersionsRoute';
import { AssignmentSelectStrategyRoute } from 'borrower/routes/assignment/AssignmentSelectStrategyRoute';
import CreditFacilityProvider from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { LatestAssignmentPipelineRunMetadataForLpGroupingProvider } from 'packages/loanProduct/hooks/LatestAssignmentPipelineRunMetadataForLpGroupingProvider';
import { LoanProductCreditFacilityGroupProvider } from 'packages/loanProduct/hooks/LoanProductCreditFacilityGroupingProvider';
import { AssignByIndividual } from 'packages/loanProduct/ui/v2/AssignByIndividual';
import { AssignmentSummary as AssignmentSummaryV2 } from 'packages/loanProduct/ui/v2/AssignmentSummary';

export const AssignmentRouter = withLDConsumer()(({ flags }) => {
  const { data, loading } = useGetUserQuery();
  const isProduction = useMemo(() => commonUtils.EnvUtils.isProduction(), []);

  if (loading || !data || !data.user) {
    return <Skeleton />;
  }

  const { organizationId } = data.user;

  return (
    <CreditFacilityProvider
      renderLoading={<Skeleton />}
      organizationId={organizationId}
      fallbackComponent={<Redirect from="/assignments" to="/" />}
    >
      {/* @jendevelops TODO move to route once cutover to v2 is complete */}
      <LoanProductCreditFacilityGroupProvider
        organizationId={organizationId}
        renderLoading={<Skeleton />}
        fallbackComponent={<Skeleton />}
      >
        <LatestAssignmentPipelineRunMetadataForLpGroupingProvider
          renderLoading={<Skeleton />}
        >
          <Switch>
            <Route path="/assignments/summary">
              <AssignmentSummaryV2 organizationId={organizationId} />
            </Route>
            {/* @jendevelops TODO replace polling route with jobId in url params */}
            <Route
              exact
              path="/assignments/summary/:previewIdentifier/:fileVersion/:reportingDate"
            >
              <AssignmentSummaryV2 organizationId={organizationId} />
            </Route>
            {!isProduction && (
              <Route path="/assignments/v2/edit">
                <AssignByIndividual organizationId={organizationId} />
              </Route>
            )}
            {flags?.assignByIndividualReceivable && (
              <Route
                exact
                path="/assignments/edit"
                component={AssignmentEditRoute}
              />
            )}
            <Route
              exact
              path="/assignments/edit-by-csv"
              component={AssignmentEditByCSVRoute}
            />
            <Route
              exact
              path="/assignments/strategy"
              component={AssignmentSelectStrategyRoute}
            />

            <Route
              exact
              path="/assignments/strategy/:strategyId/apply"
              component={AssignmentApplyStrategyRoute}
            />

            <Route exact path="/assignments/history">
              <AssignmentSavedVersionsRoute />
            </Route>
            <Redirect from="/assignments" to="/assignments/summary" />
          </Switch>
        </LatestAssignmentPipelineRunMetadataForLpGroupingProvider>
      </LoanProductCreditFacilityGroupProvider>
    </CreditFacilityProvider>
  );
});
